import { Button } from '@fox/atoms/src/button/index';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { Skeleton } from '@fox/atoms/src/skeleton/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import { FlexRowSpacedRightAligned } from '@fox/layout/src/flex';
import { nanoid } from 'nanoid';
import React, { useEffect, useRef, useState } from 'react';
import { PaymentAddComponentProps, PaymentOptionalCvv } from '../../../contracts';
import { useEnsureScript } from '../../shared';

export type EWayEnvironmentSettings = { sandbox?: boolean; clientEncryption: string; publicApiKey: string };

declare global {
	interface Window {
		eWAY: any;
	}
}

export function EWayAddCreditCard({
	onSuccess,
	completeIntent,
	intentData,
	settings,
	onCancel,
	showSave,
	optionalCvv
}: PaymentAddComponentProps & PaymentOptionalCvv) {
	const ready = useEnsureScript(`https://secure.ewaypayments.com/scripts/eWAY.min.js`);
	const tempData = useTempDataContext();

	const eWaySettings = settings as EWayEnvironmentSettings;
	const publicApiKey = eWaySettings?.publicApiKey;
	const intentId = intentData?.intentId;
	const defaultConfigError = !publicApiKey ? 'API Key missing' : undefined;
	const [errors, setErrors] = useState<string[]>(intentId ? [] : ['Error occurred initialising add card form.']);
	const [configError, setConfigError] = useState<string | undefined>(defaultConfigError);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const targetId = useRef('eWayFormTarget');

	const [childKey, setChildKey] = useState<number>(1);

	if (!ready) return <Skeleton />;

	if (configError)
		return (
			<>
				<MuiAlert severity="error">
					Invalid EWay Configuration
					<br />
					{configError ? configError : null}
				</MuiAlert>
				<FlexRowSpacedRightAligned>
					<Button color="secondary" variant="secondary" onClick={() => onCancel?.()}>
						Cancel
					</Button>
					<Button color="info" variant="secondary" onClick={() => setChildKey(prev => prev + 1)}>
						Retry
					</Button>
				</FlexRowSpacedRightAligned>
			</>
		);

	const handleSubmit = async (secureEWayData: string) => {
		if (!intentId || !secureEWayData) return;
		setIsSubmitting(true);

		return new Promise((resolve, reject) => {
			completeIntent(intentId, secureEWayData)
				.then(result => {
					if (tempData) tempData.cardFormDataValid = true;
					onSuccess(result!);
					resolve(result);
				})
				.catch(result => {
					if (tempData) tempData.cardFormDataValid = false;
					setErrors(['Unable to save credit card details']);
					reject(result);
				})
				.finally(() => {
					setIsSubmitting(false);
				});
		});
	};

	return (
		<EWayCardComponent
			key={`eway-internal-${childKey}`}
			formTarget={targetId.current}
			publicApiKey={publicApiKey}
			setConfigErrors={setConfigError}
			errors={errors}
			optionalCvv={optionalCvv}
			onCancel={onCancel}
			showSave={showSave}
			isSubmitting={isSubmitting}
			handleSubmit={handleSubmit}
			forceReload={() => {
				setChildKey(prev => prev + 1);
				setConfigError(defaultConfigError);
			}}
		/>
	);
}

function EWayCardComponent({
	formTarget,
	publicApiKey,
	setConfigErrors,
	errors,
	optionalCvv,
	onCancel,
	showSave,
	isSubmitting,
	handleSubmit,
	forceReload
}: {
	formTarget: string;
	publicApiKey: string;
	setConfigErrors: React.Dispatch<React.SetStateAction<string | undefined>>;
	errors: string[];
	onCancel: (() => void) | undefined;
	showSave: boolean | undefined;
	isSubmitting: boolean;
	handleSubmit: (secureEWayData: string) => Promise<unknown>;
	forceReload: () => void;
} & PaymentOptionalCvv) {
	const tempData = useTempDataContext();
	const elementIDSuffix = useRef(nanoid());
	const [secureEWayData, setSecureEWayData] = useState<string | undefined>();
	const [loaded, setLoaded] = useState<boolean>(false);

	//(Copied from CardForm)
	// TODO - Clean this up so it's less hacky
	React.useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: submit
		})
	);

	function secureFieldCallback(event) {
		if (!event.fieldValid) {
			const eWayErrors = (event.errors as string).split(',');
			if (eWayErrors.includes('V6143')) {
				setConfigErrors('API Key is invalid.');
				return;
			}
			//Secure code has expired - reload form
			if (eWayErrors.includes('V6148')) {
				forceReload();
				return;
			}
			setConfigErrors('Unknown error occurred in card form.');
			return;
		} else {
			setSecureEWayData(event.secureFieldCode);
		}
	}

	const submit = async e => {
		e?.preventDefault?.();
		if (!secureEWayData) return;
		await handleSubmit(secureEWayData);
	};

	/**
	 * We need to provide field styles to EWay - each field is its own iFrame that gets rendered
	 * and this controls how those fields are displayed.
	 */
	const fieldStyles = 'line-height: 1; border: 1px solid #AAA; color: #000;';
	const nameFieldConfig = {
		publicApiKey: publicApiKey,
		fieldDivId: `eway-secure-field-name-${elementIDSuffix.current}`,
		fieldType: 'name',
		styles: fieldStyles + ' width: 99%; padding: 8.5px 14px'
	};
	const cardFieldConfig = {
		publicApiKey: publicApiKey,
		fieldDivId: `eway-secure-field-card-${elementIDSuffix.current}`,
		fieldType: 'card',
		styles: fieldStyles + ' width: 99%; padding: 8.5px 14px'
	};
	//The expiry field is one iFrame with two elements - one for month select, one for year select
	const expiryFieldConfig = {
		publicApiKey: publicApiKey,
		fieldDivId: `eway-secure-field-expiry-${elementIDSuffix.current}`,
		fieldType: 'expiry',
		styles: fieldStyles + ' margin-right:0.5rem; padding: 2.5px 5px 2.5px 6px'
	};
	const cvnFieldConfig = {
		publicApiKey: publicApiKey,
		fieldDivId: `eway-secure-field-cvn-${elementIDSuffix.current}`,
		fieldType: 'cvn',
		styles: fieldStyles + ' width: 45%; padding: 8.5px 14px'
	};

	function loadEWayFields() {
		window.eWAY.setupSecureField(nameFieldConfig, secureFieldCallback);
		window.eWAY.setupSecureField(cardFieldConfig, secureFieldCallback);
		window.eWAY.setupSecureField(expiryFieldConfig, secureFieldCallback);
		if (optionalCvv) window.eWAY.setupSecureField(cvnFieldConfig, secureFieldCallback);
	}

	useEffect(() => {
		if (loaded) return;
		loadEWayFields();
		setLoaded(true);
	});
	/**
	 * We are setting the height of the divs here to 2.5rem since otherwise the divs get too large with a bunch of whitespace
	 * I need to check whether this will cause issues with 3DS.
	 */
	return (
		<>
			<form method="POST" onSubmit={submit} id="payment_form" target={formTarget}>
				<Typography>Card Name:</Typography>
				<div style={{ height: '2.5rem' }} id={`eway-secure-field-name-${elementIDSuffix.current}`}></div>
				<Typography>Card Number:</Typography>
				<div style={{ height: '2.5rem' }} id={`eway-secure-field-card-${elementIDSuffix.current}`}></div>
				<Typography>Card Expiry:</Typography>
				<div style={{ height: '2.5rem' }} id={`eway-secure-field-expiry-${elementIDSuffix.current}`}></div>
				{optionalCvv && (
					<>
						<Typography>Card CVN:</Typography>
						<div style={{ height: '2.5rem' }} id={`eway-secure-field-cvn-${elementIDSuffix.current}`}></div>
					</>
				)}
				{errors?.length ? (
					<MuiAlert color="error" severity="error">
						{errors.map(a => (
							<React.Fragment key={a}>
								{a}
								<br />
							</React.Fragment>
						))}
					</MuiAlert>
				) : null}
				<FlexRowSpacedRightAligned>
					{onCancel ? (
						<Button loading={isSubmitting} color="secondary" variant="secondary" onClick={() => onCancel()}>
							Cancel
						</Button>
					) : null}
					{showSave ? (
						<Button type="submit" loading={isSubmitting} color="primary" variant="primary">
							Save
						</Button>
					) : null}
				</FlexRowSpacedRightAligned>
			</form>
		</>
	);
}
